import {
	IIncident,
	ISortIncidentData,
	IVehicleCreateIncident,
	IVehicleCreateIncidentSection1,
	IVehicleCreateIncidentSection3,
	IVehicleCreateIncidentSection3Payload,
	IVehicleCreateIncidentSection4,
	IVehicleCreateIncidentSection4Payload,
	IVehicleCreateIncidentSection5,
	IVehicleCreateIncidentSection5Payload,
	IVehicleIncident,
	IVehicleIncidentSection1Payload,
	IVehicleIncidentSection2,
	IVehicleIncidentSection2Payload,
	IVehicleIncidentSection3Payload,
	IVehicleIncidentSection4Payload,
	IVehicleIncidentSection5,
	IVehicleIncidentUpdate,
	IIncidentCategoryBreakdown,
	IIncidentCategoryBreakdownFilters,
	IVehicleCreateIncidentSection7Payload,
	IVehicleCreateIncidentSection7,
	IVehicleIncidentSection7,
	IVehicleIncidentSection6,
	IDashboardOverviewData,
	IIncidentSeverityBreakdown,
	IIncidentMechanismBreakdown,
	IIncidentAgencyBreakdown,
	IIncidentOpenIncidentsBreakdown,
	IIncidentCriticalActionsBreakdown,
} from 'types/VehicleIncidents';

import { baseAPI } from './baseAPI';

interface IAssignPayload {
	id: number;
	body: {
		status: 'new' | 'open' | 'closed' | 'draft';
		assignee: string | undefined;
	};
}

interface ISpecialStatusPayload {
	id: number;
	body: {
		special_status: 'duplicate' | 'nuisance' | null;
	};
}

export interface IVehicleIncidentActivity {
	content: string;
	createdAt: Date;
	id: number;
	type: 'comment' | 'status' | 'assign';
	user?: {
		id: string;
		first_name: string;
		last_name: string;
		avatar?: {
			url: string;
		};
	};
}

export interface IVehicleIncidentCommentPayload {
	incident?: number;
	type: 'comment' | 'view report' | 'export';
	content: string;
	user?: number;
}

export interface VehiclePagination {
	page: number;
	pageSize: number;
	pageCount: number;
	total: number;
}

export interface IAlloc8SyncStats {
	days: number;
	totalIncidents: number;
	newIncidents: number;
	openIncidents: number;
	duplicateIncidents: number;
	nuisanceIncidents: number;
	mappingFailuresByDate: {
		date: string;
		count: number;
	}[];
}

export const inventoryAPI = baseAPI.injectEndpoints({
	endpoints: (builder) => ({
		getBranches: builder.query<{ id: string; label: string }[], void>({
			query() {
				return {
					url: '/branches',
					method: 'GET',
				};
			},
		}),
		getVehicleIncidents: builder.mutation<
			{
				sortData: ISortIncidentData[];
				data: IVehicleIncident[];
				pagination: VehiclePagination;
				prepend?: boolean;
			},
			string
		>({
			query(period) {
				return {
					url: `/incidents?${period}`,
					method: 'GET',
				};
			},
		}),
		getAllIncidentSections: builder.query<any, { id: number }>({
			query(payload) {
				return {
					url: `/incidents/all-sections/${payload.id}`,
					method: 'GET',
				};
			},
		}),
		createIncident: builder.mutation<
			IVehicleCreateIncident,
			{ branch: number | null }
		>({
			query(payload) {
				return {
					url: `/incidents`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		updateIncident: builder.mutation<void, IVehicleIncidentUpdate>({
			query(payload) {
				return {
					url: `/incidents/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncident: builder.mutation<IIncident, number>({
			query(payload) {
				return {
					url: `/incidents/${payload}`,
					method: 'GET',
				};
			},
		}),
		getIncidentForCompletionCheck: builder.mutation<IIncident, number>({
			query(payload) {
				return {
					url: `/incidents/${payload}`,
					method: 'GET',
				};
			},
		}),
		assignIncident: builder.mutation<
			void,
			IAssignPayload | ISpecialStatusPayload
		>({
			query(payload) {
				return {
					url: `/incident/status/${payload.id}`,
					method: 'PUT',
					body: payload.body,
				};
			},
		}),
		getIncidentActivity: builder.mutation<IVehicleIncidentActivity[], number>({
			query(incidentId) {
				return {
					url: `/incidents/${incidentId}/activity`,
					method: 'GET',
				};
			},
		}),
		CreateIncidentActivity: builder.mutation<
			void,
			IVehicleIncidentCommentPayload
		>({
			query(payload) {
				return {
					url: `/incident-activities`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection1: builder.mutation<
			{ data: IVehicleIncidentSection1Payload },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-1s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		createIncidentSection1: builder.mutation<
			IVehicleCreateIncidentSection1,
			IVehicleIncidentSection1Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-1s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		createIncidentSection2: builder.mutation<
			IVehicleIncidentSection2,
			{ id: number }
		>({
			query(payload) {
				return {
					url: `/incident-section-2s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		createIncidentSection3: builder.mutation<
			IVehicleCreateIncidentSection3,
			IVehicleCreateIncidentSection3Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-3s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		createIncidentSection4: builder.mutation<
			IVehicleCreateIncidentSection4,
			IVehicleCreateIncidentSection4Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-4s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		createIncidentSection5: builder.mutation<
			IVehicleCreateIncidentSection5,
			IVehicleCreateIncidentSection5Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-5s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		createIncidentSection7: builder.mutation<
			IVehicleCreateIncidentSection7,
			IVehicleCreateIncidentSection7Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-7s`,
					method: 'POST',
					body: { data: payload },
				};
			},
		}),
		updateIncidentSection1: builder.mutation<
			void,
			IVehicleIncidentSection1Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-1s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection2: builder.mutation<
			{ data: IVehicleIncidentSection2 },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-2s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection2: builder.mutation<
			void,
			IVehicleIncidentSection2Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-2s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection3: builder.mutation<
			{ data: IVehicleIncidentSection3Payload },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-3s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection3: builder.mutation<
			void,
			IVehicleIncidentSection3Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-3s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection4: builder.mutation<
			{ data: IVehicleIncidentSection4Payload },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-4s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection4: builder.mutation<
			void,
			IVehicleIncidentSection4Payload
		>({
			query(payload) {
				return {
					url: `/incident-section-4s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection5: builder.mutation<
			{ data: IVehicleIncidentSection5 },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-5s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection5: builder.mutation<
			void,
			IVehicleIncidentSection5 | { id: number; isReportFinished: true }
		>({
			query(payload) {
				return {
					url: `/incident-section-5s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection6: builder.mutation<
			{ data: IVehicleIncidentSection6 },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-6s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection6: builder.mutation<
			void,
			IVehicleIncidentSection6 | { id: number }
		>({
			query(payload) {
				return {
					url: `/incident-section-6s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		getIncidentSection7: builder.mutation<
			{ data: IVehicleIncidentSection7; branch: { id: number; name: string } },
			number
		>({
			query(incident_id) {
				return {
					url: `/incident-section-7s/${incident_id}`,
					method: 'GET',
				};
			},
		}),
		updateIncidentSection7: builder.mutation<
			any,
			IVehicleIncidentSection7 | { id: number }
		>({
			query(payload) {
				return {
					url: `/incident-section-7s/${payload.id}`,
					method: 'PUT',
					body: { data: payload },
				};
			},
		}),
		deleteIncidentActivity: builder.mutation<void, number>({
			query(activityId) {
				return {
					url: `/incident-activities/${activityId}`,
					method: 'DELETE',
				};
			},
		}),
		updateIncidentActivity: builder.mutation<
			void,
			{ activityId: number; content: string }
		>({
			query(payload) {
				return {
					url: `/incident-activities/${payload.activityId}`,
					method: 'PUT',
					body: payload,
				};
			},
		}),
		getBreakdownByCategory: builder.mutation<
			IIncidentCategoryBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/category-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getPermissions: builder.mutation<ISortIncidentData, void>({
			query() {
				return {
					url: `/permissions/get`,
					method: 'GET',
				};
			},
		}),
		getDashboardOverview: builder.query<
			IDashboardOverviewData,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/dashboard/overview?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdown: builder.query<
			IIncidentCategoryBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/category-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownString: builder.query<
			IIncidentCategoryBreakdown,
			string
		>({
			query(filters) {
				return {
					url: `/incidents/category-breakdown-filtered?${filters}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownBySeverity: builder.query<
			IIncidentSeverityBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/severity-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownByMechanism: builder.query<
			IIncidentMechanismBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/mechanism-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownByOpenIncidents: builder.query<
			IIncidentOpenIncidentsBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/open-incidents-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownByCriticalActions: builder.query<
			IIncidentCriticalActionsBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/critical-actions-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getDashboardBreakdownByAgency: builder.query<
			IIncidentAgencyBreakdown,
			IIncidentCategoryBreakdownFilters
		>({
			query(filters) {
				// @ts-ignore
				const urlString = new URLSearchParams(filters).toString();

				return {
					url: `/incidents/agency-breakdown?${urlString}`,
					method: 'GET',
				};
			},
		}),
		getAlloc8SyncStats: builder.query<
			IAlloc8SyncStats,
			{ days?: number } | void
		>({
			query(params) {
				const days = params && 'days' in params ? params.days : undefined;
				const query = days ? `?days=${days}` : '';

				return {
					url: `/incidents/alloc8-sync-stats${query}`,
					method: 'GET',
				};
			},
		}),
	}),
});

export const {
	useGetBranchesQuery,
	useGetVehicleIncidentsMutation,
	useGetIncidentMutation,
	useGetIncidentForCompletionCheckMutation,
	useGetAllIncidentSectionsQuery,
	useAssignIncidentMutation,
	useGetIncidentActivityMutation,
	useCreateIncidentActivityMutation,
	useCreateIncidentMutation,
	useUpdateIncidentMutation,
	useGetIncidentSection1Mutation,
	useCreateIncidentSection1Mutation,
	useUpdateIncidentSection1Mutation,
	useGetIncidentSection2Mutation,
	useUpdateIncidentSection2Mutation,
	useGetIncidentSection3Mutation,
	useUpdateIncidentSection3Mutation,
	useGetIncidentSection4Mutation,
	useUpdateIncidentSection4Mutation,
	useGetIncidentSection5Mutation,
	useUpdateIncidentSection5Mutation,
	useGetIncidentSection6Mutation,
	useUpdateIncidentSection6Mutation,
	useGetIncidentSection7Mutation,
	useUpdateIncidentSection7Mutation,
	useCreateIncidentSection2Mutation,
	useCreateIncidentSection3Mutation,
	useCreateIncidentSection4Mutation,
	useCreateIncidentSection5Mutation,
	useUpdateIncidentActivityMutation,
	useDeleteIncidentActivityMutation,
	useGetBreakdownByCategoryMutation,
	useGetPermissionsMutation,
	useGetDashboardOverviewQuery,
	useGetDashboardBreakdownQuery,
	useGetDashboardBreakdownStringQuery,
	useGetDashboardBreakdownBySeverityQuery,
	useGetDashboardBreakdownByMechanismQuery,
	useGetDashboardBreakdownByAgencyQuery,
	useGetDashboardBreakdownByOpenIncidentsQuery,
	useGetDashboardBreakdownByCriticalActionsQuery,
	useGetAlloc8SyncStatsQuery,
} = inventoryAPI;
